import { GetStaticProps } from 'next';
import ShopCategory from '../../[category]';
import { wcApiDataService } from 'functions/wcApi';
import { filterLensTypes } from 'functions/filterLensTypes';
import { getSeoMetaTags, getSeoBlockContent, getPageHeaderData, getCategoryBannerImages } from 'utils/common.utils';
import { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import { BreadcrumbItem } from 'components/Breadcrumb/Breadcrumb';
import { frameShapeToName } from 'functions/frameShapeToName';
import { getCategoryTitle } from 'functions/categoryTitle';
import { changeSunglasses } from 'functions/changeSunglasses';

export default ShopCategory;

export const getStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  let { category, shape, colour } = params as { category: string; shape: string; colour?: string };

  if (category === 'sunglasses') {
    category = 'non-prescription-sunglasses';
  }

  const categoryData = await wcApiDataService(`cat/${category}`, '', locale);
  const productDetailPageData = await wcApiDataService(`glasses/${category}`, '', locale);

  if (!categoryData) {
    return {
      notFound: true
    };
  }

  const glassesProductData = productDetailPageData?.components;
  const categoryLensTypes = filterLensTypes(glassesProductData);

  const seoMetaTags = await getSeoMetaTags(`/shop/${category}/shape/${shape}`, locale);
  const seoBlock = await getSeoBlockContent('/shop/' + category + '/shape/' + shape, locale);
  const categoryBannerImages = await getCategoryBannerImages(category, locale);

  const frameShapeName = frameShapeToName(shape);
  const colorTitle = getCategoryTitle({ colour, query: { shape } });

  const pageTitle = colorTitle
    ? `${changeSunglasses(categoryData.name, { capitalize: true })} - ${changeSunglasses(colorTitle, { capitalize: true })}Frames | Dresden Vision`
    : `${changeSunglasses(categoryData.name, { capitalize: true })} - Frames | Dresden Vision`;

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    },
    {
      id: 3,
      href: `shop/${category}`,
      text: categoryData.name
    },
    {
      id: 4,
      href: `shop/${category}/shape/${shape}`,
      text: `${frameShapeName} Frames`
    }
  ];

  if (colour) {
    breadcrumbItems.push({
      id: 5,
      href: `shop/${category}/shape/${shape}/colour/${colour}`,
      text: `${colorTitle}Frames`
    });
  }

  const categoryBannerProps: CategoryBannerProps = {
    title: colorTitle
      ? `${changeSunglasses(categoryData.name, { capitalize: true })} - ${frameShapeName} ${changeSunglasses(colorTitle, { capitalize: true })}Frames`
      : `${changeSunglasses(categoryData.name, { capitalize: true })} - ${frameShapeName} Frames`,
    description: categoryData.description,
    imageSrc: `https://w.dresden.vision/images/categories/${category}/banner.jpg`,
    imageAlt: colorTitle
      ? `${changeSunglasses(categoryData.name, { capitalize: true })} - ${frameShapeName} ${changeSunglasses(colorTitle, { capitalize: true })}frames`
      : `${changeSunglasses(categoryData.name, { capitalize: true })} - ${frameShapeName} frames`,
    breadcrumbItems: breadcrumbItems
  };

  const notionPageHeaders = await getPageHeaderData(category + '/shape/' + shape, locale, categoryBannerProps);
  if (notionPageHeaders == categoryBannerProps) {
    const mainPageHeader = await getPageHeaderData(category, locale, categoryBannerProps);
    notionPageHeaders.imageSrc = mainPageHeader?.imageSrc ?? '';
    notionPageHeaders.imageAlt = mainPageHeader?.imageAlt ?? '';
    notionPageHeaders.videoSrc = mainPageHeader?.videoSrc ?? '';
  }

  if (notionPageHeaders?.title) {
    notionPageHeaders.title = changeSunglasses(notionPageHeaders.title, { capitalize: true });
  }

  return {
    props: {
      categoryData,
      seoBlock,
      seoMetaTags,
      categoryLensTypes,
      categoryBannerProps: notionPageHeaders,
      categoryBannerImages,
      pageTitle
    }
  };
};
